import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Button, CircularProgress, TablePagination } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { ColumnDef } from "@tanstack/react-table";
import {
  TanStackDataTable,
  FilterOptions,
  ActiveFilters,
  SortState,
} from "./TanStackDataTable";

// Raw data interface
export interface RawData {
  ORDERNUM: string;
  GANTT_TYPE: string;
  JOB_ID: string;
  APPOINTMENTDATE: string;
  SERVICEPOWER_STATUS: string;
  PPTPROFIT_FINAL: number;
  PPT_PROFIT_RANGE: string;
  IRU: string;
  OPERATIVE: string;
  MDS_CD_DESRIPTION: string;
  PRIORITY: number;
  IMPORTANCE: number;
  TRAVEL: number;
  DURATION: number;
  JEOPARDY_GROUP: string;
  JEOPARDY_REASON: string;
  REGION_CODE: string;
  DISTRICT_NAME: string;
  PLANNING_AREA_NAME: string;
  HS_SP_CD_DESCRIPTION: string;
  "Call Type": string;
  CLIENT_NM: string;
  SO_CRT_DT: string;
  N_ATP: number;
  DAYS_TO_RECALL: number;
  CYCLE_TIME: number;
  RESPONSE_TIME: number;
  TIME_ZONE_NAME: string;
  TOTAL_RESCHEDULES: number;
  SEARS_INITIATED: number;
  CUSTOMER_INITIATED: number;
}

// Filterable columns - all columns will have filter dropdowns
const FILTERABLE_COLUMNS = [
  "ORDERNUM",
  "GANTT_TYPE",
  "JOB_ID",
  "APPOINTMENTDATE",
  "SERVICEPOWER_STATUS",
  "PPTPROFIT_FINAL",
  "PPT_PROFIT_RANGE",
  "IRU",
  "OPERATIVE",
  "MDS_CD_DESRIPTION",
  "PRIORITY",
  "IMPORTANCE",
  "TRAVEL",
  "DURATION",
  "JEOPARDY_GROUP",
  "JEOPARDY_REASON",
  "REGION_CODE",
  "DISTRICT_NAME",
  "PLANNING_AREA_NAME",
  "HS_SP_CD_DESCRIPTION",
  "Call Type",
  "CLIENT_NM",
  "SO_CRT_DT",
  "N_ATP",
  "DAYS_TO_RECALL",
  "CYCLE_TIME",
  "RESPONSE_TIME",
  "TIME_ZONE_NAME",
  "TOTAL_RESCHEDULES",
  "SEARS_INITIATED",
  "CUSTOMER_INITIATED",
];

// Column config for raw data table
const RAW_DATA_COLUMN_CONFIG: {
  key: keyof RawData;
  header: string;
  width: number;
  format?: "currency" | "number" | "date";
}[] = [
  { key: "ORDERNUM", header: "Order #", width: 120 },
  { key: "GANTT_TYPE", header: "Gantt Type", width: 100 },
  { key: "JOB_ID", header: "Type", width: 70 },
  { key: "APPOINTMENTDATE", header: "Appt Date", width: 110 },
  { key: "SERVICEPOWER_STATUS", header: "Status", width: 90 },
  { key: "PPTPROFIT_FINAL", header: "Profit", width: 90, format: "currency" },
  { key: "PPT_PROFIT_RANGE", header: "Profit Range", width: 110 },
  { key: "OPERATIVE", header: "Tech Name", width: 120 },
  { key: "IRU", header: "IRU", width: 80 },
  { key: "REGION_CODE", header: "Region", width: 90 },
  { key: "DISTRICT_NAME", header: "District", width: 130 },
  { key: "PLANNING_AREA_NAME", header: "Planning Area", width: 160 },
  { key: "HS_SP_CD_DESCRIPTION", header: "Appliance", width: 120 },
  { key: "MDS_CD_DESRIPTION", header: "Merchandise Code", width: 150 },
  { key: "Call Type", header: "Call Type", width: 110 },
  { key: "CLIENT_NM", header: "Client", width: 130 },
  { key: "SO_CRT_DT", header: "SO Create Date", width: 130 },
  { key: "IMPORTANCE", header: "Importance", width: 110 },
  { key: "TRAVEL", header: "Travel Duration", width: 80 },
  { key: "DURATION", header: "Repair Duration", width: 95 },
  { key: "N_ATP", header: "Trips", width: 80 },
  { key: "DAYS_TO_RECALL", header: "Days to Recall", width: 130 },
  { key: "CYCLE_TIME", header: "Cycle Time", width: 110 },
  { key: "RESPONSE_TIME", header: "Response Time", width: 130 },
  { key: "TOTAL_RESCHEDULES", header: "Total Reschedules", width: 140 },
  { key: "SEARS_INITIATED", header: "Sears Initiated", width: 130 },
  { key: "CUSTOMER_INITIATED", header: "Customer Initiated", width: 150 },
  { key: "TIME_ZONE_NAME", header: "Time Zone", width: 120 },
  { key: "JEOPARDY_GROUP", header: "Reason Group", width: 160 },
  { key: "JEOPARDY_REASON", header: "Reason Description", width: 280 },
];

interface DrillDownFilters {
  hourBucket: string;
  category: string;
  filterType: string;
}

interface RawDataTableProps {
  onRefresh?: () => void;
  // External data mode props (for drill-down usage)
  externalData?: RawData[];
  externalLoading?: boolean;
  externalTotal?: number;
  drillDownFilters?: DrillDownFilters;
  title?: string;
  showDownload?: boolean;
  showPagination?: boolean;
  maxHeight?: string;
  // Custom column widths - key is column key, value is width in pixels
  columnWidths?: Record<string, number>;
  // Timezone filter (array for multiselect)
  timezones?: string[];
  // Call type filter (array for multiselect)
  callTypes?: string[];
  // Planning area filter (array for multiselect)
  planningAreas?: string[];
  // Appointment date filter (array for multiselect)
  apptDates?: string[];
  // Runtime date filter
  runtimeDate?: string;
  // Gantt type filter (array for multiselect)
  ganttTypes?: string[];
}

export const RawDataTable: React.FC<RawDataTableProps> = ({
  onRefresh,
  externalData,
  externalLoading,
  externalTotal,
  drillDownFilters,
  title,
  showDownload = true,
  showPagination = true,
  maxHeight = "500px",
  columnWidths,
  timezones = [],
  callTypes = [],
  planningAreas = [],
  apptDates = [],
  runtimeDate = "",
  ganttTypes = [],
}) => {
  // Determine if we're in external data mode
  const isExternalMode = externalData !== undefined;

  const [rawData, setRawData] = useState<RawData[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [displayHour, setDisplayHour] = useState<string | null>(null);
  const [displayDate, setDisplayDate] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // Server-side filter and sort state
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
  const [sortState, setSortState] = useState<SortState | null>(null);

  // Fetch filter options for dropdown filters
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (runtimeDate) params.append("runtimeDate", runtimeDate);
      if (timezones.length > 0) params.append("timezones", timezones.join(","));
      if (callTypes.length > 0) params.append("callTypes", callTypes.join(","));
      if (planningAreas.length > 0)
        params.append("planningAreas", planningAreas.join(","));
      if (apptDates.length > 0) params.append("apptDates", apptDates.join(","));
      if (ganttTypes.length > 0)
        params.append("ganttTypes", ganttTypes.join(","));
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await fetch(
        `/api/snowflake/latest-hour-raw-data/filters${queryString}`,
      );
      const result = await response.json();
      if (result.success) {
        // Format APPOINTMENTDATE values as YYYY-MM-DD
        const filters = { ...result.filters };
        if (filters.APPOINTMENTDATE) {
          filters.APPOINTMENTDATE = filters.APPOINTMENTDATE.map((val: any) => {
            if (!val) return val;
            // Handle date objects or ISO strings
            if (val instanceof Date) {
              return val.toISOString().split("T")[0];
            }
            if (typeof val === "string" && val.includes("T")) {
              return val.split("T")[0];
            }
            // Handle "Mon Dec 29 2025..." format
            if (typeof val === "string" && /^[A-Z][a-z]{2}\s/.test(val)) {
              const d = new Date(val);
              if (!isNaN(d.getTime())) {
                return d.toISOString().split("T")[0];
              }
            }
            return String(val);
          });
        }
        setFilterOptions(filters);
      }
    } catch (err: any) {
      console.error("Error fetching filter options:", err);
    }
  }, [runtimeDate, timezones, callTypes, planningAreas, apptDates, ganttTypes]);

  // Fetch raw data from API with server-side filtering, sorting, and pagination
  const fetchRawData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/snowflake/latest-hour-raw-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: page + 1,
          limit: rowsPerPage,
          filters: activeFilters,
          sortBy: sortState?.column || null,
          sortOrder: sortState?.direction?.toUpperCase() || null,
          runtimeDate: runtimeDate || null,
          timezones: timezones.length > 0 ? timezones : null,
          callTypes: callTypes.length > 0 ? callTypes : null,
          planningAreas: planningAreas.length > 0 ? planningAreas : null,
          apptDates: apptDates.length > 0 ? apptDates : null,
          ganttTypes: ganttTypes.length > 0 ? ganttTypes : null,
        }),
      });
      const result = await response.json();

      if (result.success) {
        setRawData(result.data);
        setTotal(result.pagination?.total || 0);
        setDisplayHour(result.hourBucket || null);
        setDisplayDate(result.runtimeDate || null);
      } else {
        console.error("Failed to fetch raw data:", result.error);
      }
    } catch (err: any) {
      console.error("Error fetching raw data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    activeFilters,
    sortState,
    runtimeDate,
    timezones,
    callTypes,
    planningAreas,
    apptDates,
    ganttTypes,
  ]);

  // Handle page change
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter change - reset to page 0 when filters change
  const handleFilterChange = (newFilters: ActiveFilters) => {
    setActiveFilters(newFilters);
    setPage(0);
  };

  // Handle sort change - reset to page 0 when sort changes
  const handleSortChange = (newSort: SortState | null) => {
    setSortState(newSort);
    setPage(0);
  };

  // Download filtered raw data as CSV
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await fetch(
        "/api/snowflake/latest-hour-raw-data/download",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filters: activeFilters,
            sortBy: sortState?.column || null,
            sortOrder: sortState?.direction?.toUpperCase() || null,
            runtimeDate: runtimeDate || null,
            timezones: timezones.length > 0 ? timezones : null,
            callTypes: callTypes.length > 0 ? callTypes : null,
            planningAreas: planningAreas.length > 0 ? planningAreas : null,
            apptDates: apptDates.length > 0 ? apptDates : null,
            ganttTypes: ganttTypes.length > 0 ? ganttTypes : null,
          }),
        },
      );
      const result = await response.json();

      if (result.success && result.data.length > 0) {
        // Convert to CSV
        const headers = Object.keys(result.data[0]);
        const csvContent = [
          headers.join(","),
          ...result.data.map((row: any) =>
            headers
              .map((h) => {
                const val = row[h];
                if (val == null) return "";
                const strVal = String(val);
                // Escape quotes and wrap in quotes if contains comma
                if (strVal.includes(",") || strVal.includes('"')) {
                  return `"${strVal.replace(/"/g, '""')}"`;
                }
                return strVal;
              })
              .join(","),
          ),
        ].join("\n");

        // Create download link
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `latest_hour_raw_data_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      console.error("Error downloading raw data:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Reset page and filters when filter arrays change
  useEffect(() => {
    setPage(0);
    setActiveFilters({});
  }, [runtimeDate, timezones, callTypes, planningAreas, apptDates]);

  // Fetch filter options - needed for both modes to enable column filters
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Refetch raw data when pagination, filters, or sort changes - only in non-external mode
  useEffect(() => {
    if (!isExternalMode) {
      fetchRawData();
    }
  }, [fetchRawData, isExternalMode]);

  // Expose refresh method via onRefresh callback
  useEffect(() => {
    if (onRefresh) {
      // This is a workaround to expose refresh - parent can call fetchRawData
    }
  }, [onRefresh]);

  // Generate filter options from external data for client-side filtering
  const externalFilterOptions = useMemo<FilterOptions>(() => {
    if (!isExternalMode || !externalData) return {};

    const options: FilterOptions = {};
    FILTERABLE_COLUMNS.forEach((col) => {
      const uniqueValues = new Set<string>();
      externalData.forEach((row) => {
        const val = row[col as keyof RawData];
        if (val != null) {
          uniqueValues.add(String(val));
        }
      });
      options[col] = Array.from(uniqueValues).sort();
    });
    return options;
  }, [isExternalMode, externalData]);

  // Apply client-side filtering and sorting for external mode
  const filteredExternalData = useMemo(() => {
    if (!isExternalMode || !externalData) return externalData;

    let filtered = [...externalData];

    // Apply filters
    Object.entries(activeFilters).forEach(([columnId, filterValues]) => {
      if (filterValues.length > 0) {
        filtered = filtered.filter((row) => {
          const cellValue = String(row[columnId as keyof RawData] ?? "");
          return filterValues.includes(cellValue);
        });
      }
    });

    // Apply sorting
    if (sortState) {
      filtered.sort((a, b) => {
        const aVal = a[sortState.column as keyof RawData];
        const bVal = b[sortState.column as keyof RawData];

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        let comparison = 0;
        if (typeof aVal === "number" && typeof bVal === "number") {
          comparison = aVal - bVal;
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sortState.direction === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [isExternalMode, externalData, activeFilters, sortState]);

  // Use external filter options when in external mode
  const displayFilterOptions = isExternalMode
    ? externalFilterOptions
    : filterOptions;

  // Compute display data based on mode
  const displayData = isExternalMode ? filteredExternalData : rawData;
  const displayLoading = isExternalMode ? (externalLoading ?? false) : loading;
  const displayTotal = isExternalMode
    ? (filteredExternalData?.length ?? 0)
    : total;
  // Build dynamic title showing date, hour, and record count
  const buildTitle = () => {
    if (title) return title;
    if (isExternalMode)
      return `Raw Data (${displayTotal.toLocaleString()} records)`;
    return `Latest Hour Raw Data (${displayTotal.toLocaleString()} records)`;
  };
  const displayTitle = buildTitle();

  // Download all drill-down data as CSV (fetches all from server)
  const handleExternalDownload = async () => {
    if (!drillDownFilters) return;

    setDownloading(true);
    try {
      // Fetch all data from server (no pagination limit)
      const response = await fetch("/api/snowflake/by-hour/drill-down", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hourBucket: drillDownFilters.hourBucket,
          category: drillDownFilters.category,
          filterType: drillDownFilters.filterType,
          runtimeDate: runtimeDate || null,
          timezones: timezones.length > 0 ? timezones : null,
          callTypes: callTypes.length > 0 ? callTypes : null,
          planningAreas: planningAreas.length > 0 ? planningAreas : null,
          apptDates: apptDates.length > 0 ? apptDates : null,
          page: 1,
          limit: 10000, // Large limit to get all data
        }),
      });
      const result = await response.json();

      if (result.success && result.data.length > 0) {
        const headers = RAW_DATA_COLUMN_CONFIG.map((col) => col.header);
        const csvRows = [headers.join(",")];

        result.data.forEach((row: any) => {
          const values = RAW_DATA_COLUMN_CONFIG.map((col) => {
            const val = row[col.key];
            if (val == null) return "";
            const strVal = String(val).replace(/"/g, '""');
            return `"${strVal}"`;
          });
          csvRows.push(values.join(","));
        });

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `drill_down_${drillDownFilters.category.replace(/\s+/g, "_")}_${drillDownFilters.hourBucket.replace(":", "")}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error downloading data:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Column definitions for raw data table
  const columns = useMemo<ColumnDef<RawData>[]>(
    () =>
      RAW_DATA_COLUMN_CONFIG.map((col) => {
        const colDef: ColumnDef<RawData, any> = {
          id: col.key,
          accessorFn: (row) => row[col.key],
          header: col.header,
          size: columnWidths?.[col.key] ?? col.width,
          cell: ({ getValue }) => {
            const val = getValue();
            if (val == null) return "";

            // Custom renderer for Order # column - link to SharePoint
            if (col.key === "ORDERNUM") {
              const orderNum = String(val);
              const sharePointUrl = `https://searshc.sharepoint.com/sites/Koyfin/Shared%20Documents/index.aspx/home/order-lookup?ORDERNUM=${encodeURIComponent(orderNum)}`;
              return (
                <a
                  href={sharePointUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.textDecoration = "none")
                  }
                >
                  {orderNum}
                </a>
              );
            }

            if (col.format === "currency") {
              const num = Number(val);
              if (num < 0) {
                return `-$${Math.abs(num).toFixed(2)}`;
              }
              return `$${num.toFixed(2)}`;
            }
            return String(val);
          },
        };
        return colDef;
      }),
    [columnWidths],
  );

  return (
    <Box sx={{ mt: isExternalMode ? 0 : 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: isExternalMode ? "flex-end" : "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        {!isExternalMode && (
          <Box
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#1e293b",
            }}
          >
            {displayTitle}
          </Box>
        )}
        {showDownload && (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloading ? <CircularProgress size={16} /> : <DownloadIcon />
            }
            onClick={isExternalMode ? handleExternalDownload : handleDownload}
            disabled={downloading || displayTotal === 0}
            sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.5 }}
          >
            {downloading ? "Downloading..." : "Download CSV"}
          </Button>
        )}
      </Box>
      <>
        <TanStackDataTable
          data={displayData || []}
          columns={columns}
          loading={displayLoading}
          maxHeight={maxHeight}
          expandableColumnId=""
          enableFilters={true}
          filterableColumns={FILTERABLE_COLUMNS}
          filterOptions={displayFilterOptions}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          enableSorting={true}
          sortState={sortState}
          onSortChange={handleSortChange}
          enableVirtualization={true}
          rowHeight={24}
          pinFirstColumn={true}
        />
        {showPagination && !isExternalMode && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100, 200]}
            sx={{
              borderTop: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
              "& .MuiTablePagination-toolbar": {
                minHeight: 36,
                height: 36,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.75rem",
                },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      </>
    </Box>
  );
};

export default RawDataTable;
